import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Lock, Shield } from 'lucide-react';

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { adminLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await adminLogin(email, password);

// result might be { user, access_token } OR just user depending what you return
const user = result?.user || result;
const role = (user?.role || '').toLowerCase();

toast.success('Login successful!');

if (['admin', 'developer', 'outreach'].includes(role)) {
  navigate('/admin/dashboard', { replace: true });
} else {
  // if you want staff to have their own area:
  navigate('/staff', { replace: true });
  // or if staff should still use admin dashboard:
  // navigate('/admin/dashboard', { replace: true });
}
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error(error.response?.data?.detail || 'Admin login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-500/10 mb-4">
            <Shield className="text-indigo-400" size={32} />
          </div>
          <h1 className="text-3xl font-bold font-['Syne'] text-white mb-2" data-testid="admin-login-title">
            Admin Portal
          </h1>
          <p className="text-slate-400">Secure access for administrators only</p>
        </div>

        <div className="glass rounded-2xl p-8 glow">
          <form onSubmit={handleSubmit} data-testid="admin-login-form">
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@dhhomenet.co.uk"
                    required
                    data-testid="admin-email-input"
                    className="pl-10 bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="admin-password-input"
                    className="pl-10 bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit-btn"
                className="w-full bg-white text-black hover:bg-slate-200 font-semibold py-3 rounded-full transition-all duration-300"
              >
                {loading ? 'Signing In...' : 'Sign In as Admin'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-400">
              Client? <Link to="/login" className="text-indigo-400 hover:text-indigo-300" data-testid="client-login-link">Login here</Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-400 hover:text-white transition-colors text-sm" data-testid="back-home-link">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
